import { findMinBy, mapEvery } from '@seaber/common/lib/collections';
import type { VesselSpeedConsumptionFragment } from '../../graphql';
import {
  BunkerGrade,
  VesselArea,
  VesselSpeedTag,
  VesselSpeedType,
} from '../../graphql';
import type { CurrencyRates } from '../../state/zustand';
import { convertMonetaryValue } from '../../state/zustand';
import type { PortCallBunkerOverride } from './Core';
import type { CalculationConfig } from './ScheduleDataContext';
import type { Vessel, VSCSByTagTypeAndArea, VSCSByTypeAndArea } from './Vessel';
import type { EnrichedSpeedConsumption } from './TimeData';
import { resolveBunkerGradeId } from './Core.resolve-bunker-grade-id';
import {
  isOrgDefaultBunkerGradeArea,
  type OrgDefaultBunkerGradeIdsByArea,
} from '../../lib/org-area-bunker-grade.utils';
import type { BunkerGradeCalculationMaps } from '../../lib/bunker-grade-mappings.utils';
import type { ResolvedBunkerGrade } from '../../lib/bunker-blend.utils';
import { nonWeakMemoize3, nonWeakMemoize4 } from '@seaber/common/lib/memoize';

/**
 * When a leg gets created, the speed of the vessel is originally fetched/set from the default speed value of ‘All areas’.
 * If the leg duration is altered by the user or ETA updates, the new speed is simply derived from the leg duration,
 * and rounded to the nearest value.
 */

// In case of bunker grade calculations getNearestSpeedConsumption could return 2 entries for PnL
export type SpeedConsumptionResult = {
  primary: EnrichedSpeedConsumption;
  secondary?: EnrichedSpeedConsumption;
};

function resolveGradeOverrides(
  resolved: ResolvedBunkerGrade | undefined,
  rawGrade:
    | { gradeName?: string; carbonFactor?: string | number | null }
    | null
    | undefined,
  rawFuelCost: number,
): {
  referenceGrade: string;
  fuelCost: number;
  carbonFactor: number | undefined;
  gradeName: string | undefined;
} {
  return {
    referenceGrade:
      resolved?.gradeName ?? rawGrade?.gradeName ?? BunkerGrade.MGO,
    fuelCost: resolved?.fuelCost ?? rawFuelCost,
    carbonFactor:
      resolved?.carbonFactor ??
      (rawGrade?.carbonFactor != null ? +rawGrade.carbonFactor : undefined),
    gradeName: resolved?.gradeName ?? rawGrade?.gradeName,
  };
}

/**
 * Reprices/relabels the leg's ECA-area sea grade to the vessel's mapped grade for
 * that area; consumption is unchanged. Returns true whenever a mapping is set —
 * even if it equals the base grade — so the caller suppresses the org-area
 * relabel. Returns false (no effect) for port legs, ALL_AREAS, or no mapping.
 */
function applyVesselBunkerGradeRelabel(
  primary: EnrichedSpeedConsumption,
  consumedBunker: number,
  type: VesselSpeedType,
  vesselArea: VesselArea,
  bunkerGradeIdsByArea: OrgDefaultBunkerGradeIdsByArea | null | undefined,
  resolvedBunkerGrades: Map<number, ResolvedBunkerGrade> | null | undefined,
): boolean {
  if (type === VesselSpeedType.PORT) return false;
  if (!isOrgDefaultBunkerGradeArea(vesselArea)) return false;

  const mappedGradeId = bunkerGradeIdsByArea?.[vesselArea];
  if (mappedGradeId == null) return false;
  // Equals base grade: suppress org relabel, nothing to change.
  if (mappedGradeId === primary.gradeId) return true;

  const resolved = resolvedBunkerGrades?.get(mappedGradeId);
  // Unpriced target: either absent from the map, or present with fuelCost 0 —
  // resolveAllBunkerGrades stores 0 when the price cannot be resolved (no price
  // and no reference grade). Relabeling to it would collapse the leg cost to
  // zero, so fall through: keep the org default and a dual-fuel secondary.
  if (!resolved?.fuelCost) return false;

  primary.gradeId = mappedGradeId;
  primary.gradeName = resolved.gradeName;
  primary.referenceGrade = resolved.gradeName;
  primary.fuelCost = resolved.fuelCost;
  primary.carbonFactor = resolved.carbonFactor ?? undefined;
  primary.voyageCost = consumedBunker * resolved.fuelCost;
  return true;
}

type SpeedConsumptionParams = {
  vesselArea: VesselArea;
  bunkerPriceByGradeAndPortCode: CalculationConfig['bunkerPriceByGradeAndPortCode'];
  calculatedHours: number;
  distance: number;
  targetSpeed: number;
  type: VesselSpeedType;
  vessel: Vessel;
  isCargoEvent?: boolean;
  isLoading?: boolean;
  currencyRates: CurrencyRates | null;
  isExtraPortConsumptionHours?: boolean;
  portBunkerOverrides: readonly PortCallBunkerOverride[] | null;
  orgDefaultBunkerGradeIdsByArea: OrgDefaultBunkerGradeIdsByArea | null;
  weatherFactor?: number | null;
  bunkerGradeCalculations?: BunkerGradeCalculationMaps | null;
  resolvedBunkerGrades?: Map<number, ResolvedBunkerGrade> | null;
};

export function getNearestSpeedConsumption(
  params: SpeedConsumptionParams,
): SpeedConsumptionResult | undefined {
  const result = computeNearestSpeedConsumption(params);
  if (!result) return undefined;
  const { primary, secondary, usedOverride } = result;

  // An active override suppresses the grade the row would consume by default
  // (e.g. an org-area substituted grade). Evaluate the pipeline once more
  // override-free so the P&L grade dropdown keeps offering that grade.
  if (usedOverride) {
    const defaults = computeNearestSpeedConsumption({
      ...params,
      portBunkerOverrides: null,
    });
    if (defaults) {
      primary.defaultGradeId = defaults.primary.gradeId;
      primary.defaultGradeName =
        defaults.primary.gradeName ?? defaults.primary.referenceGrade;
      if (secondary && defaults.secondary) {
        secondary.defaultGradeId = defaults.secondary.gradeId;
        secondary.defaultGradeName =
          defaults.secondary.gradeName ?? defaults.secondary.referenceGrade;
      }
    }
  }

  return { primary, secondary };
}

function computeNearestSpeedConsumption(
  params: SpeedConsumptionParams,
): (SpeedConsumptionResult & { usedOverride: boolean }) | undefined {
  const {
    vesselArea,
    calculatedHours,
    distance,
    bunkerPriceByGradeAndPortCode,
    targetSpeed,
    type,
    vessel,
    isLoading,
    isCargoEvent,
    currencyRates,
    isExtraPortConsumptionHours,
    portBunkerOverrides,
    orgDefaultBunkerGradeIdsByArea,
    weatherFactor,
    bunkerGradeCalculations,
    resolvedBunkerGrades,
  } = params;
  const {
    speedConsumptions,
    speedConsumptionsByTag,
    defaultBunkerGradeId,
    bunkerGradeIdsByArea,
  } = vessel;
  const resolveParams = {
    portBunkerOverrides,
    vesselArea,
    type,
    isExtraPortConsumptionHours,
    isCargoEvent,
    isLoading,
    speedConsumptionsByTag,
    orgDefaultBunkerGradeIdsByArea,
    vesselDefaultBunkerGradeId: defaultBunkerGradeId,
  };
  const { bunkerGradeId, tag, isFromOverride } =
    resolveBunkerGradeId(resolveParams);

  if (bunkerGradeId === undefined) {
    return;
  }

  // When weather factor is present, use STW (water speed) for consumption lookup
  // STW = SOG * (1 + weatherFactor/100)
  const wf = weatherFactor ?? 0;
  const lookupSpeed = targetSpeed * (1 + wf / 100);

  const lookupHit = (gradeId: number) => {
    const gradeNodes = getSpeedConsumptionsForTagAndType(
      tag,
      type,
      speedConsumptions,
      speedConsumptionsByTag,
      gradeId,
    );
    if (!gradeNodes) {
      return null;
    }
    const { hit: gradeHit } = getClosestSpeedHit(
      gradeNodes,
      vesselArea,
      gradeId,
      lookupSpeed,
    );
    return gradeHit === null ? null : { nodes: gradeNodes, hit: gradeHit };
  };

  /*
   * An overridden grade may have no speed & consumption rows of its own, e.g.
   * a calculation-substituted grade chosen from the Voyage P&L dropdown. The
   * chosen grade keeps its identity (name, price, emissions) but consumes on
   * the rows of the grade the row would resolve to without the override.
   */
  let consumptionGradeId = bunkerGradeId;
  let lookup = lookupHit(consumptionGradeId);
  if (lookup === null && isFromOverride) {
    const { bunkerGradeId: defaultGradeId } = resolveBunkerGradeId({
      ...resolveParams,
      portBunkerOverrides: null,
    });
    if (defaultGradeId !== undefined) {
      lookup = lookupHit(defaultGradeId);
      if (lookup !== null) {
        consumptionGradeId = defaultGradeId;
      }
    }
  }
  if (lookup === null) {
    return;
  }
  const { nodes, hit } = lookup;

  const resolvedGrade = resolvedBunkerGrades?.get(bunkerGradeId);
  const { fuelCost: rawFuelCost, bunkerGrade } = calculateFuelCost(
    hit,
    bunkerPriceByGradeAndPortCode,
    currencyRates,
  );
  const gradeOverrides = resolveGradeOverrides(
    resolvedGrade,
    bunkerGrade,
    rawFuelCost,
  );

  const matchedSpeed = Number(hit.speed);
  const consumption = +hit.consumption || 0;

  /*
   * The weather factor only selects the STW consumption row (lookupSpeed above);
   * it must not switch off idle detection. To find the idle portion we convert
   * the matched STW back to a ground speed with the same factor, so a slow leg's
   * true sailing time is measured even under weather. At wf=0 this reduces to
   * distance / matchedSpeed, identical to the pre-weather behaviour. Port calls
   * have no sailing distance, so their whole duration stays as-is. The
   * matchedSpeed === 0 fallback divides by targetSpeed, which is already SOG and
   * needs no weather conversion — the (1 + wf/100) factor exists only to turn the
   * matched STW row back into a ground speed; it just guards a degenerate
   * zero-speed consumption row.
   */
  const sailingHoursAtMatchedSpeed =
    type === VesselSpeedType.PORT
      ? calculatedHours
      : matchedSpeed > 0
        ? (distance * (1 + wf / 100)) / matchedSpeed
        : distance / targetSpeed;

  /*
   * Idle is the surplus over min-speed sailing, booked only when the leg's
   * required STW cannot reach the vessel's slowest sea row — the true table
   * minimum, not the closest-matched row that getClosestSpeedHit returns.
   * Gating on the true minimum keeps an at/above-minimum weather leg from
   * booking idle merely because its STW falls between two curve rows.
   */
  const minTableSpeed = Math.min(
    ...getSpeedConsumptionsForAreaAndBunker(
      nodes,
      vesselArea,
      consumptionGradeId,
    ).map((s) => +s.speed),
  );
  const LEG_IDLE_MIN_HOURS = 1;
  const hoursDiff = calculatedHours - sailingHoursAtMatchedSpeed;
  const idleHours =
    lookupSpeed < minTableSpeed && hoursDiff >= LEG_IDLE_MIN_HOURS
      ? hoursDiff
      : 0;

  /*
   * calculatedHours already encodes user-set seaDurationOverride and weather
   * factor from upstream, so the displayed leg duration follows STW changes.
   * On a below-minimum leg the sailing portion is the at-minimum sailing time;
   * the surplus is charged separately upstream as port consumption via idleHours.
   */
  const actualHours = calculatedHours - idleHours;

  // calculate the consumption
  const consumedBunker = (actualHours * consumption) / 24;

  // calculate the cost
  const voyageCost = consumedBunker * gradeOverrides.fuelCost;

  const primary: EnrichedSpeedConsumption = {
    duration: actualHours,
    distance,
    consumption,
    ...gradeOverrides,
    consumedBunker,
    voyageCost,
    idleHours,
    gradeId: bunkerGradeId,
    targetArea: vesselArea,
  };

  // Apply the per-vessel ECA override; a manual per-leg override wins. When it
  // applies it fixes the primary grade, so the org-area relabel below is
  // suppressed — but the dual-fuel secondary must still be computed.
  const vesselRelabelApplied =
    !isFromOverride &&
    applyVesselBunkerGradeRelabel(
      primary,
      consumedBunker,
      type,
      vesselArea,
      bunkerGradeIdsByArea,
      resolvedBunkerGrades,
    );

  // Check both forward mapping and reverse map for additional bunker consumption
  const targetBunkerGrade = bunkerGradeCalculations?.map.get(bunkerGradeId);
  const reverseTargetBunkerGrade =
    bunkerGradeCalculations?.reverseMap.get(bunkerGradeId);

  const {
    bunkerGradeId: overrideSecondaryGradeId,
    isFromOverride: secondaryIsFromOverride,
  } = resolveBunkerGradeId({
    portBunkerOverrides,
    vesselArea,
    type,
    isExtraPortConsumptionHours,
    isCargoEvent,
    isLoading,
    speedConsumptionsByTag,
    orgDefaultBunkerGradeIdsByArea,
    vesselDefaultBunkerGradeId: undefined,
    isPrimary: false,
  });

  const effectiveSecondaryGradeId =
    overrideSecondaryGradeId ?? targetBunkerGrade?.id;

  // If there is a bunker grade mapping, adjust primary properties and calculate secondary
  let secondary: EnrichedSpeedConsumption | undefined = undefined;
  if (effectiveSecondaryGradeId) {
    // Calculate secondary
    const auxGradeId = effectiveSecondaryGradeId;
    const auxNodes = getSpeedConsumptionsForTagAndType(
      tag,
      type,
      speedConsumptions,
      speedConsumptionsByTag,
      auxGradeId,
    );
    let actualTargetGrade = targetBunkerGrade;
    if (auxNodes) {
      const { hit: auxHit } = getClosestSpeedHit(
        auxNodes,
        vesselArea,
        auxGradeId,
        targetSpeed,
      );
      if (auxHit) {
        actualTargetGrade =
          auxHit.bunkerGradeOverrideByBunkerGradeId ?? targetBunkerGrade;
        const secondaryGradeId =
          actualTargetGrade?.id ?? effectiveSecondaryGradeId;
        const resolvedSecondary = resolvedBunkerGrades?.get(secondaryGradeId);
        const modifiedAuxHit = {
          ...auxHit,
          bunkerGradeOverrideByBunkerGradeId: actualTargetGrade,
        } as VesselSpeedConsumptionFragment;
        const { fuelCost: rawAuxFuelCost } = calculateFuelCost(
          modifiedAuxHit,
          bunkerPriceByGradeAndPortCode,
          currencyRates,
        );
        const secondaryOverrides = resolveGradeOverrides(
          resolvedSecondary,
          actualTargetGrade,
          rawAuxFuelCost,
        );
        const auxConsumption = +auxHit.consumption || 0;
        const auxConsumedBunker = (actualHours * auxConsumption) / 24;
        const auxVoyageCost = auxConsumedBunker * secondaryOverrides.fuelCost;

        secondary = {
          duration: actualHours,
          distance,
          consumption: auxConsumption,
          ...secondaryOverrides,
          consumedBunker: auxConsumedBunker,
          voyageCost: auxVoyageCost,
          idleHours: 0,
          gradeId: secondaryGradeId,
          targetArea: vesselArea,
        };
      }
    }

    const gradeDiffersFromOrgAreaDefault =
      orgDefaultBunkerGradeIdsByArea != null &&
      primary.targetArea !== VesselArea.ALL_AREAS &&
      orgDefaultBunkerGradeIdsByArea[primary.targetArea] !== primary.gradeId;

    // If no secondary, use target grade properties for primary (fallback).
    // Skipped when a per-vessel override already fixed the primary grade, and
    // when the grade came from an explicit per-leg override — a manually chosen
    // grade is applied as-is and must not be re-substituted by the org calc.
    if (
      !secondary &&
      !isFromOverride &&
      !vesselRelabelApplied &&
      actualTargetGrade &&
      gradeDiffersFromOrgAreaDefault
    ) {
      const resolvedTarget = resolvedBunkerGrades?.get(actualTargetGrade.id);
      const modifiedHit = {
        ...hit,
        bunkerGradeOverrideByBunkerGradeId: actualTargetGrade,
      } as VesselSpeedConsumptionFragment;
      const { fuelCost: rawNewFuelCost } = calculateFuelCost(
        modifiedHit,
        bunkerPriceByGradeAndPortCode,
        currencyRates,
      );
      const targetOverrides = resolveGradeOverrides(
        resolvedTarget,
        actualTargetGrade,
        rawNewFuelCost,
      );
      primary.referenceGrade = targetOverrides.referenceGrade;
      primary.fuelCost = targetOverrides.fuelCost;
      primary.gradeId = actualTargetGrade.id;
      primary.gradeName = targetOverrides.gradeName;
      primary.carbonFactor = targetOverrides.carbonFactor;
      primary.voyageCost = consumedBunker * targetOverrides.fuelCost;
    }
  } else if (reverseTargetBunkerGrade && !isFromOverride) {
    // Calculate secondary using source grade consumption; a per-row override
    // is applied as-is without spawning the mapped source grade alongside it
    const auxGradeId = reverseTargetBunkerGrade.id;
    const auxNodes = getSpeedConsumptionsForTagAndType(
      tag,
      type,
      speedConsumptions,
      speedConsumptionsByTag,
      auxGradeId,
    );
    if (auxNodes) {
      const { hit: auxHit } = getClosestSpeedHit(
        auxNodes,
        vesselArea,
        auxGradeId,
        targetSpeed,
      );
      if (auxHit) {
        const modifiedAuxHit = {
          ...auxHit,
          bunkerGradeOverrideByBunkerGradeId: bunkerGrade, // Use current grade properties for secondary
        };
        const { fuelCost: rawAuxFuelCost } = calculateFuelCost(
          modifiedAuxHit,
          bunkerPriceByGradeAndPortCode,
          currencyRates,
        );
        const reverseOverrides = resolveGradeOverrides(
          resolvedGrade,
          bunkerGrade,
          rawAuxFuelCost,
        );
        const auxConsumption = +auxHit.consumption || 0;
        const auxConsumedBunker = (actualHours * auxConsumption) / 24;
        const auxVoyageCost = auxConsumedBunker * reverseOverrides.fuelCost;

        secondary = {
          duration: actualHours,
          distance,
          consumption: auxConsumption,
          ...reverseOverrides,
          consumedBunker: auxConsumedBunker,
          voyageCost: auxVoyageCost,
          idleHours: 0,
          gradeId: bunkerGradeId,
          targetArea: vesselArea,
        };
      }
    }
  }

  return {
    primary,
    secondary,
    usedOverride: isFromOverride || secondaryIsFromOverride,
  };
}

const calculateFuelCost = nonWeakMemoize3(function calculateFuelCost(
  hit: VesselSpeedConsumptionFragment,
  bunkerPriceByGradeAndPortCode: CalculationConfig['bunkerPriceByGradeAndPortCode'],
  currencyRates: CurrencyRates | null,
) {
  let fuelCost = 0;

  // get the fuel cost if there is an override if not fetch it from bunkerEx
  const bunkerGrade = hit.bunkerGradeOverrideByBunkerGradeId;
  if (bunkerGrade) {
    const referenceGrade = bunkerGrade.referenceGrade;
    const referencePort = bunkerGrade.referencePort;
    const priceAdjustment = +(bunkerGrade.priceAdjustment ?? 0);
    const bunkerExPrice =
      bunkerPriceByGradeAndPortCode?.get(referenceGrade)?.get(referencePort)
        ?.latestPrices[0]?.price ?? 0;

    const price = +(
      bunkerGrade.price ??
      (referenceGrade && referencePort ? bunkerExPrice + priceAdjustment : 0)
    );

    // convert price to default currency
    fuelCost =
      convertMonetaryValue(
        price,
        bunkerGrade.currency ?? 'USD',
        currencyRates,
      ) ?? 0;
  }
  return { fuelCost, bunkerGrade };
});

/**
 * Get all speed consumption nodes that match a bunker grade with area preference.
 * Returns nodes in order of preference: current area, then ALL_AREAS, then other areas.
 * Removes duplicates by speed value. Sorted by speed ascending.
 */
export const getSpeedConsumptionsForAreaAndBunker = nonWeakMemoize3(
  function getSpeedConsumptionsForAreaAndBunker(
    nodes: Map<VesselArea | null, VesselSpeedConsumptionFragment[]>,
    vesselArea: VesselArea,
    bunkerGradeId: number,
  ): VesselSpeedConsumptionFragment[] {
    const otherArea =
      vesselArea === VesselArea.INSIDE_ECA
        ? VesselArea.OUTSIDE_ECA
        : VesselArea.INSIDE_ECA;

    /** First try to match in current area, then ALL_AREAS, then any area */
    const speedPreferences = [
      nodes.get(vesselArea),
      nodes.get(VesselArea.ALL_AREAS),
      nodes.get(otherArea),
    ];

    const allMatching: VesselSpeedConsumptionFragment[] = [];
    const seenSpeeds = new Set<number>();

    for (const speeds of speedPreferences) {
      if (!speeds) continue;

      for (const speedConsumption of speeds) {
        if (
          speedConsumption.bunkerGradeOverrideByBunkerGradeId?.id !==
          bunkerGradeId
        )
          continue;

        const speed = +speedConsumption.speed;
        if (isNaN(speed)) continue;

        // Avoid duplicates by speed value
        if (!seenSpeeds.has(speed)) {
          seenSpeeds.add(speed);
          allMatching.push(speedConsumption);
        }
      }
    }

    return allMatching;
  },
);

export const getClosestSpeedHit = nonWeakMemoize4(function getClosestSpeedHit(
  nodes: Map<VesselArea | null, VesselSpeedConsumptionFragment[]>,
  vesselArea: VesselArea,
  bunkerGradeId: number,
  targetSpeed: number,
) {
  const allMatching = getSpeedConsumptionsForAreaAndBunker(
    nodes,
    vesselArea,
    bunkerGradeId,
  );

  const hit =
    findMinBy(allMatching, (speedConsumption) =>
      Math.abs(targetSpeed - +speedConsumption.speed),
    ) ?? null;

  const minSpeed = hit?.speed ? +hit.speed : Infinity;

  return { hit, minSpeed };
});

export function getSpeedConsumptionsForTagAndType(
  tag: VesselSpeedTag | 'leg',
  type: VesselSpeedType,
  speedConsumptions: VSCSByTypeAndArea,
  speedConsumptionsByTag: VSCSByTagTypeAndArea,
  bunkerGradeId: number,
) {
  let nodes = (
    tag === 'leg' ? speedConsumptions : speedConsumptionsByTag.get(tag)
  )?.get(type);

  const hasValidNodes = (nds: typeof nodes) =>
    !!nds &&
    // There must be at least one value for the matching bunker grade in the map
    !mapEvery(
      nds,
      (v) =>
        !v.some(
          (c) => c.bunkerGradeOverrideByBunkerGradeId?.id === bunkerGradeId,
        ),
    );

  // If there are no values for the tag, use the default tag
  if (!hasValidNodes(nodes)) {
    nodes = speedConsumptionsByTag.get(VesselSpeedTag.DEFAULT)?.get(type);
    // If there are no values for the default tag, use untagged
    if (!hasValidNodes(nodes)) {
      nodes = speedConsumptionsByTag.get(null)?.get(type);
    }
  }

  return nodes;
}
